// PIN entry: dots + keypad. Used by the kiosk PIN step and the TOTP step.
// Two size presets from the spec (phone 56px keys / board 70px keys).

import type { CSSProperties } from 'react';

export interface PinDotsProps {
  length: number;
  filled: number;
  error?: boolean;
  size?: 'phone' | 'board';
}

export function PinDots({ length, filled, error = false, size = 'phone' }: PinDotsProps) {
  const dot = size === 'board' ? 18 : 16;
  const gap = size === 'board' ? 16 : 15;
  return (
    <div
      style={{
        display: 'flex',
        justifyContent: 'center',
        gap,
        animation: error ? 'ipShake .4s' : 'none',
      }}
    >
      {Array.from({ length }, (_, i) => (
        <span
          key={i}
          style={{
            width: dot,
            height: dot,
            borderRadius: '50%',
            background: i < filled ? 'var(--ink)' : 'transparent',
            border: `2px solid ${i < filled ? 'var(--ink)' : 'var(--line)'}`,
          }}
        />
      ))}
      <span className="sr-only" aria-live="polite">
        {filled} of {length} digits entered
      </span>
    </div>
  );
}

export interface NumberPadProps {
  onKey: (digit: string) => void;
  onDelete: () => void;
  size?: 'phone' | 'board';
  disabled?: boolean;
}

export function NumberPad({ onKey, onDelete, size = 'phone', disabled = false }: NumberPadProps) {
  const board = size === 'board';
  const keyStyle: CSSProperties = {
    height: board ? 70 : 56,
    borderRadius: board ? 18 : 16,
    background: 'var(--surface)',
    boxShadow: 'var(--elev)',
    color: 'var(--ink)',
    font: `600 ${board ? 28 : 24}px 'Space Grotesk',sans-serif`,
    display: 'grid',
    placeItems: 'center',
    userSelect: 'none',
  };

  return (
    <div
      role="group"
      aria-label="PIN pad"
      style={{
        display: 'grid',
        gridTemplateColumns: board ? 'repeat(3, 88px)' : 'repeat(3, 1fr)',
        gap: board ? 14 : 12,
        justifyContent: 'center',
      }}
    >
      {['1', '2', '3', '4', '5', '6', '7', '8', '9'].map((d) => (
        <button
          key={d}
          type="button"
          className="press-hard"
          disabled={disabled}
          onClick={() => onKey(d)}
          style={keyStyle}
        >
          {d}
        </button>
      ))}
      <span />
      <button type="button" className="press-hard" disabled={disabled} onClick={() => onKey('0')} style={keyStyle}>
        0
      </button>
      <button
        type="button"
        className="press-hard"
        disabled={disabled}
        onClick={onDelete}
        aria-label="Delete last digit"
        style={{ ...keyStyle, background: 'transparent', boxShadow: 'none' }}
      >
        ⌫
      </button>
    </div>
  );
}
